/**
 * Custom fetch mutator used by the Orval-generated client.
 * Same-origin requests: the dev preview and production server both serve
 * the API under /api, so no base URL is required.
 *
 * When authentication is enabled the frontend registers a token provider
 * (see setAuthTokenProvider); the mutator then attaches a bearer token to
 * every request. Tokens stay in memory — never persisted here.
 */
import { captureSession } from './session-boundary';
import type { BookingOverlapWarning, PolicyAdmissionDetails } from './index';

export type PolicyAdmissionIntent = 'booking' | 'check-in';
export type PolicyRequirementPrompt = (details: PolicyAdmissionDetails, approvalId: string | undefined, intent: PolicyAdmissionIntent) => void;
let policyPrompt: PolicyRequirementPrompt | null = null;
/** Display a rejected admission without replaying its request. */
export function setPolicyRequirementPrompt(prompt: PolicyRequirementPrompt | null): void { policyPrompt = prompt; }
import { genericApplicationMessage } from './error-message';
import { groupOperations } from './group-operations.generated';

export type BookingOverlapReviewer = (warning: BookingOverlapWarning) => Promise<string | null>;
let overlapReviewer: BookingOverlapReviewer | null = null;

/** The application supplies one accessible, explicit review surface. */
export function setBookingOverlapReviewer(reviewer: BookingOverlapReviewer | null): void {
  overlapReviewer = reviewer;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly policyRequirements: PolicyAdmissionDetails | undefined;

  constructor(message: string, status: number, code?: string, policyRequirements?: PolicyAdmissionDetails) {
    super(genericApplicationMessage(message));
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.policyRequirements = policyRequirements;
  }
}

export type AuthTokenProvider = () => Promise<string | null>;
/** Optional demoted product-role code (mirrors server X-View-As-Role). */
export type ViewAsRoleProvider = () => string | null;

let tokenProvider: AuthTokenProvider | null = null;
let managerGraphTokenProvider: AuthTokenProvider | null = null;
type GroupProvider = { token: AuthTokenProvider; identity: () => string | null };
let groupGraphProvider: GroupProvider | null = null;

/** Request-local downstream access; identity is read synchronously at every fence. */
export function setGroupGraphTokenProvider(provider: AuthTokenProvider | null, identity: () => string | null = () => null): void {
  groupGraphProvider = provider ? { token: provider, identity } : null;
}

const groupMatchers = groupOperations.map(([method, template]) => ({
  method,
  segments: template.split('/')
}));
function isGroupOperation(url: string, method = 'GET'): boolean {
  const path = url.split('?')[0] ?? '';
  if (!path.startsWith('/api/') || /[%\\\\#]/.test(path) || url.includes('#')) return false;
  const segments = path.split('/');
  if (segments.some(segment => segment === '.' || segment === '..')) return false;
  return groupMatchers.some(entry => entry.method === method.toUpperCase() &&
    entry.segments.length === segments.length && entry.segments.every((segment, index) =>
      segment.startsWith('{') ? Boolean(segments[index]) : segment === segments[index]));
}

/** Downstream credential for manager scope/attendance reads only; never API authentication. */
export function setManagerGraphTokenProvider(provider: AuthTokenProvider | null): void {
  managerGraphTokenProvider = provider;
}

function isManagerDirectoryRead(url: string, method: string | undefined): boolean {
  if ((method ?? 'GET').toUpperCase() !== 'GET') return false;
  // Relative same-origin generated endpoints only. Never forward credentials to absolute URLs.
  const path = url.split('?')[0];
  return path === '/api/me/manager-scope' || path === '/api/reports/manager-attendance' ||
    /^\/api\/admin\/managers\/[^/?#]+\/scope$/.test(path ?? '');
}
let viewAsRoleProvider: ViewAsRoleProvider | null = null;

/** Register (or clear) the function used to obtain bearer tokens. */
export function setAuthTokenProvider(provider: AuthTokenProvider | null): void {
  tokenProvider = provider;
}

/**
 * Register (or clear) the function that supplies a temporary “view as”
 * product role for high-auth demotion. Null / missing ⇒ no header.
 */
export function setViewAsRoleProvider(provider: ViewAsRoleProvider | null): void {
  viewAsRoleProvider = provider;
}

/** Resolve the current bearer token, or null when auth is disabled. */
export async function getAuthToken(): Promise<string | null> {
  const session = captureSession();
  if (!tokenProvider) return null;
  try {
    const token = await tokenProvider();
    session.assertCurrent();
    return token;
  } catch {
    return null;
  }
}

function getViewAsRoleHeader(): string | null {
  if (!viewAsRoleProvider) return null;
  try {
    const role = viewAsRoleProvider()?.trim().toLowerCase();
    return role ? role : null;
  } catch {
    return null;
  }
}

/** Resolve the active tenant from the browser route without persisting it. */
function getTenantSlugHeader(): string | null {
  if (typeof window === 'undefined') return null;
  const match = /^\/t\/([a-z0-9](?:[a-z0-9-]{0,62}))(?:\/|$)/i.exec(window.location.pathname);
  return match?.[1]?.toLowerCase() ?? null;
}

async function buildHeaders(options: RequestInit): Promise<HeadersInit> {
  const isJsonBody = typeof options.body === 'string';
  const token = await getAuthToken();
  const viewAs = getViewAsRoleHeader();
  const tenantSlug = getTenantSlugHeader();
  return {
    Accept: 'application/json',
    ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(viewAs ? { 'X-View-As-Role': viewAs } : {}),
    ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
    ...options.headers
  };
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; code?: number };
  return e.name === 'AbortError' || e.code === 20;
}

export type RequestOptions = RequestInit & {
  /** Recheck a caller's scoped operation after token acquisition, before dispatch. */
  assertContext?: () => void;
  /** Explicit operation cancellation, independently of ordinary observer churn. */
  respectAbortSignal?: boolean;
};

export const customFetch = async <T>(url: string, options: RequestOptions = {}): Promise<T> => {
  // Ordinary observer cancellation remains opt-in; session cancellation is mandatory.
  const session = captureSession(options.respectAbortSignal ? options.signal : undefined);
  const check = () => { session.assertCurrent(); options.assertContext?.(); };
  // Ordinary observer churn may finish shared reads. Explicit operation
  // cancellation and retirement of the whole session always abort transport
  // work and reject obsolete token, response and review results.
  const { signal, respectAbortSignal, assertContext, ...baseOptions } = options;
  const fetchOptions = { ...baseOptions, signal: session.signal };
  if (respectAbortSignal && signal?.aborted) throw new DOMException('Operation cancelled.', 'AbortError');
  let response: Response;
  const originEntity = getTenantSlugHeader();
  const originRole = getViewAsRoleHeader();
  const originReviewer = overlapReviewer;
  const originPolicyPrompt = policyPrompt;
  try {
    const originTokenProvider = tokenProvider;
    const originGraphProvider = managerGraphTokenProvider;
    const originGroupProvider = groupGraphProvider;
    const originIdentity = originGroupProvider?.identity();
    const groupOperation = isGroupOperation(url, fetchOptions.method);
    const assertGroupContext = () => {
      if (groupOperation && (originGroupProvider !== groupGraphProvider ||
        originTokenProvider !== tokenProvider || originIdentity !== originGroupProvider?.identity() ||
        originEntity !== getTenantSlugHeader() || originRole !== getViewAsRoleHeader())) {
        throw new ApiError('Your organisation or account changed. Review and submit again.', 409, 'GROUP_CONTEXT_CHANGED');
      }
    };
    const headers = new Headers(await buildHeaders(fetchOptions));
    check();
    // Caller options cannot introduce a credential into unrelated requests.
    headers.delete('X-Manager-Graph-Token');
    headers.delete('X-Group-Graph-Token');
    assertGroupContext();
    // Demotion still needs group admission; the server independently denies administrative authority.
    if (groupOperation) {
      const groupToken = await originGroupProvider?.token().catch(() => null);
      check();
      assertGroupContext();
      if (groupToken) headers.set('X-Group-Graph-Token', groupToken);
    }
    if (isManagerDirectoryRead(url, fetchOptions.method)) {
      const graphToken = await originGraphProvider?.().catch(() => null);
      check();
      if (originTokenProvider !== tokenProvider || originGraphProvider !== managerGraphTokenProvider ||
        originEntity !== getTenantSlugHeader() || originRole !== getViewAsRoleHeader()) {
        throw new ApiError('Your reporting context changed. Apply your filters again.', 409, 'MANAGER_CONTEXT_CHANGED');
      }
      if (graphToken) headers.set('X-Manager-Graph-Token', graphToken);
    }
    assertGroupContext();
    check();
    response = await fetch(url, { ...fetchOptions, headers,
      ...(headers.has('X-Group-Graph-Token') ? { redirect: 'error' as const } : {}) });
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw err;
  }

  check();
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const body = isJson
      ? ((await response.json().catch(() => undefined)) as
          | { error?: string; code?: string; overlapWarning?: BookingOverlapWarning; policyRequirements?: PolicyAdmissionDetails }
          | undefined)
      : undefined;
    check();
    if (response.status === 409 && body?.code === 'BOOKING_OVERLAP_CONFIRMATION_REQUIRED' &&
        body.overlapWarning && overlapReviewer && typeof fetchOptions.body === 'string' &&
        fetchOptions.method?.toUpperCase() === 'POST') {
      if (originReviewer !== overlapReviewer || originEntity !== getTenantSlugHeader() ||
          originRole !== getViewAsRoleHeader()) {
        throw new ApiError('Your booking context changed. Review your choices again.', 409, 'BOOKING_CONTEXT_CHANGED');
      }
      const acknowledgement = await overlapReviewer(body.overlapWarning);
      check();
      if (!acknowledgement) {
        throw new ApiError('Booking not submitted. Your choices are saved.', 409, 'BOOKING_OVERLAP_REVIEW_CANCELLED');
      }
      if (originReviewer !== overlapReviewer || originEntity !== getTenantSlugHeader() || originRole !== getViewAsRoleHeader()) {
        throw new ApiError('Your booking context changed. Review your choices again.', 409, 'BOOKING_CONTEXT_CHANGED');
      }
      const proposal: unknown = JSON.parse(fetchOptions.body);
      if (!proposal || typeof proposal !== 'object' || Array.isArray(proposal)) {
        throw new ApiError('Invalid booking proposal.', 400, 'VALIDATION_ERROR');
      }
      // Retry the exact body and stable request ID. A fresh server warning opens
      // another review; a generic assistant Confirm never implies acknowledgement.
      return customFetch<T>(url, { ...fetchOptions, assertContext,
        body: JSON.stringify({ ...proposal, overlapAcknowledgement: acknowledgement }) });
    }
    const requirements = body?.policyRequirements;
    if (response.status === 422 && body?.code === 'POLICY_REQUIREMENTS_REQUIRED' && requirements &&
        typeof requirements.spaceId === 'string' && typeof requirements.beneficiaryUserId === 'string' &&
        typeof requirements.revision === 'string' && Array.isArray(requirements.missingRules) &&
        originPolicyPrompt === policyPrompt && originEntity === getTenantSlugHeader() && originRole === getViewAsRoleHeader()) {
      const approval = /^\/api\/visitor-self\/approvals\/([^/]+)\//.exec(url)?.[1];
      const path = url.split('?')[0] ?? '';
      const intent: PolicyAdmissionIntent = /^\/api\/bookings\/[^/]+\/check-in$/.test(path) || path === '/api/check-in/walk-up'
        ? 'check-in' : 'booking';
      policyPrompt?.(requirements, approval, intent);
    }
    throw new ApiError(
      body?.error ?? `Request failed with status ${response.status}`,
      response.status,
      body?.code,
      requirements
    );
  }

  if (!isJson) {
    // Binary endpoints (file download) resolve to a Blob.
    const body = await response.blob();
    check();
    return body as T;
  }

  const body = await response.json();
  check();
  return body as T;
};
